import React, { useCallback, useMemo } from 'react';

import { Actions, Action } from '@gogame/components';
import { useParams } from '@gogame/screens';
import { useGridApiContext } from '@gogame/data-grid';

import {
  BatchShareCreativesForm,
  BatchUnshareCreativesForm,
} from 'scenes/Gallery/GalleryPage/Creatives/BatchShareForm';
import { BulkEditCreativesForm } from 'scenes/Gallery/GalleryPage/Creatives/BulkEditForm';
import {
  useBatchArchive,
  useBatchUnarchive,
  useBatchRemove,
  useExportCreatives,
} from 'scenes/Gallery/GalleryPage/Creatives/data';
import { EXCEL, ZIP } from 'scenes/Gallery/GalleryPage/constants';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import {
  THUMBNAIL_URL,
  VIDEO_LENGTH,
} from 'scenes/Gallery/components/File/constants';

const BatchToolbar = ({ selectedRows, openChildComponent }) => {
  const [batchArchiveConcepts] = useBatchArchive();
  const [batchUnarchiveConcepts] = useBatchUnarchive();
  const [batchDelete] = useBatchRemove();
  const params = useParams();

  const {
    isAllowArchiving,
    isAllowShare,
    isAllowBatchDelete,
    isAllowCreateConcept,
  } = useGalleryPermissions();
  const [exportExcelCreatives] = useExportCreatives({
    type: EXCEL,
  });
  const [exportZipCreatives] = useExportCreatives({
    type: ZIP,
  });

  const handleExportZipCreativesSelected = useCallback(
    (selectedRows) => {
      const size = selectedRows.reduce(
        (result, { size = 0 }) => result + size,
        0
      );
      return exportZipCreatives(
        {
          ids: selectedRows.map(({ id }) => id),
          conceptId: params?.conceptId,
        },
        { size }
      );
    },
    [exportZipCreatives, params?.conceptId]
  );

  const handleExportExcelCreativesSelected = useCallback(
    (selectedRows) => {
      return exportExcelCreatives(
        {
          ids: selectedRows.map(({ id }) => id),
          conceptId: params?.conceptId,
        },
        { isAllow: selectedRows.length < 10000 }
      );
    },
    [exportExcelCreatives, params?.conceptId]
  );
  const handleBatchArchive = (selectedRows) =>
    batchArchiveConcepts(selectedRows);
  const handleBatchUnarchive = (selectedRows) =>
    batchUnarchiveConcepts(selectedRows);
  const handleBatchDelete = (selectedRows) => batchDelete(selectedRows);

  const handleBatchShare = (selectedRows) => {
    openChildComponent(BatchShareCreativesForm, {
      selectedRows,
    });
  };

  const handleBulkEdit = (selectedRows) => {
    openChildComponent(BulkEditCreativesForm, {
      selectedRows,
    });
  };

  const handleBatchUnShare = (selectedRows) => {
    openChildComponent(BatchUnshareCreativesForm, {
      selectedRows,
    });
  };

  return (
    <Actions limit={5}>
      <Action
        button
        label={'Download'}
        onClick={() => handleExportZipCreativesSelected(selectedRows)}
      />
      <Action
        button
        label={'Export to excel'}
        onClick={() => handleExportExcelCreativesSelected(selectedRows)}
      />
      {isAllowShare && (
        <Action
          button
          label={'Share'}
          onClick={() => handleBatchShare(selectedRows)}
        />
      )}
      {isAllowShare && (
        <Action
          button
          label={'Unshare'}
          onClick={() => handleBatchUnShare(selectedRows)}
        />
      )}
      {isAllowCreateConcept ? (
        <Action
          button
          label={'Bulk Edit'}
          onClick={() => handleBulkEdit(selectedRows)}
        />
      ) : null}
      {isAllowArchiving && (
        <Action
          button
          label={'Archive'}
          onClick={() => handleBatchArchive(selectedRows)}
        />
      )}
      {isAllowArchiving && (
        <Action
          button
          label={'Activate'}
          onClick={() => handleBatchUnarchive(selectedRows)}
        />
      )}
      {isAllowBatchDelete && (
        <Action
          button
          label={'Delete'}
          onClick={() => handleBatchDelete(selectedRows)}
        />
      )}
    </Actions>
  );
};

export default BatchToolbar;

export const BatchToolbarDataGrid = ({ selectedValues, ...props }) => {
  const {
    current: { getRow },
  } = useGridApiContext();

  const selectedRows = useMemo(() => {
    if (!getRow) {
      return selectedValues.map((id) => ({ id }));
    }

    return selectedValues.map((id) => {
      const {
        [CREATIVE.NAME]: name,
        [CREATIVE.FILE]: {
          [THUMBNAIL_URL]: fileThumbnailURL,
          [VIDEO_LENGTH]: fileVideoLength,
        },
        [CREATIVE.IS_THUMBNAIL_GENERATING]: isThumbnailGenerating,
      } = getRow(id);

      return {
        id,
        name,
        src: fileThumbnailURL,
        videoLength: fileVideoLength,
        thumbnail: isThumbnailGenerating,
      };
    });
  }, [selectedValues, getRow]);

  return <BatchToolbar selectedRows={selectedRows} {...props} />;
};
